import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Screen, WindowService } from '@modules/shared/services/window.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ActionIcons, ButtonItem } from '../../ui/action-container/action-container.component';
import { CardLayout } from '../enums/card-layout.enum';
import { LinkTypes } from '../enums/link-types.enum';
import { TargetType } from '../enums/target-type.enum';
import { CardLinkInterface } from '../interfaces/card-link.interface';
import { TARGET_NEW_SPECT } from '../interfaces/card.interface';
import { CardModel } from '../models/card.model';

@Component({
  selector: 'mb-card',
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<boolean>();
  private _ratio!: number;

  public linkTypes = LinkTypes;
  public cardLayout = CardLayout;

  public readonly pressKitCardType = 'presskit_card';

  @Input() layout = CardLayout.HERO;
  @Input() alignContent!: string; // 'top' | 'bottom' | 'center'
  @Input() link: CardLinkInterface | null = null;
  @Input() actions?: ButtonItem[];
  @Input() data!: CardModel;
  @Input() fuelLabelPosition: 'default' | 'bottom' = 'default';

  @Input() ratios!: { [key in Screen]: number };

  @Input()
  set ratio(ratio: number) {
    this._ratio = ratio;
    this.elRef.nativeElement.style.setProperty('--card-ratio', this.ratio);
  }
  get ratio() {
    return this._ratio;
  }

  private _layouts: { [key in Screen]?: CardLayout } = {};
  @Input()
  set layouts(v: { [key in Screen]?: CardLayout }) {
    this._layouts = v;
    this.updateLayout(this.getActiveLayout(this.windowService.screen));
  }
  get layouts() {
    return this._layouts;
  }

  private _alignContents: { [key in Screen]?: string } = {}; // 'top' | 'bottom' | 'center'
  @Input()
  set alignContents(v: { [key in Screen]?: string }) {
    this._alignContents = v;
    this.updateAlignment(this.getActiveAlignment(this.windowService.screen));
  }
  get alignContents() {
    return this._alignContents;
  }

  @Output()
  actionClick = new EventEmitter<ActionIcons>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private elRef: ElementRef,
    private windowService: WindowService,
    private cdr: ChangeDetectorRef
  ) {}

  public linkAction(e: MouseEvent) {
    const { target, query, type, url, outlets } = this.data.link as CardLinkInterface;
    const programmaticRouting = type === this.linkTypes.PROGRAMMATIC;

    if (target !== TargetType.BLANK && !programmaticRouting && !query) {
      return;
    }
    e.preventDefault();
    if (programmaticRouting || query) {
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: {
            q: query || '',
          },
          queryParamsHandling: 'merge',
        })
        .then(() => {
          this.router.navigate([url], outlets);
        });
      return false;
    }

    window.open.apply(null, [url, '_blank', TARGET_NEW_SPECT]);
    return false;
  }

  private getActiveLayout(screen: Screen) {
    if (!this._layouts) {
      return undefined;
    }
    let activeInputs = this._layouts[screen];
    if (!activeInputs) {
      const keys = Object.keys(this._layouts).map((key) => parseInt(key, 10)) as unknown as number[];
      const possibleConfigs = keys.sort((a, b) => a - b).filter((w) => w < (screen as number)) as Screen[];
      if (possibleConfigs?.length) {
        activeInputs = this._layouts[possibleConfigs[possibleConfigs.length - 1]] as any;
      }
    }
    return activeInputs;
  }

  private getActiveAlignment(screen: Screen) {
    if (!this._alignContents) {
      return undefined;
    }
    let activeInputs = this._alignContents[screen];
    if (!activeInputs) {
      const keys = Object.keys(this._alignContents).map((key) => parseInt(key, 10)) as unknown as number[];
      const possibleConfigs = keys.sort((a, b) => a - b).filter((w) => w < (screen as number)) as Screen[];
      if (possibleConfigs?.length) {
        activeInputs = this._alignContents[possibleConfigs[possibleConfigs.length - 1]] as any;
      }
    }
    return activeInputs;
  }

  private updateLayout(layout?: CardLayout) {
    if (layout) {
      this.layout = layout;
      this.cdr.detectChanges();
    }
  }

  private updateAlignment(alignment?: string) {
    if (alignment) {
      this.alignContent = alignment;
      this.cdr.detectChanges();
    }
  }

  ngOnInit() {
    this.windowService.screen$.pipe(takeUntil(this.destroy$)).subscribe((screen) => {
      if (this.ratios) {
        if (this.ratios[screen]) {
          this.ratio = this.ratios[screen];
          return;
        }
        const keys = Object.keys(this.ratios).map((key) => parseInt(key, 10)) as unknown as number[];
        const possibleConfigs = keys.sort((a, b) => a - b).filter((w) => w < (screen as number)) as Screen[];
        if (possibleConfigs?.length) {
          this.ratio = this.ratios[possibleConfigs[possibleConfigs.length - 1]];
        }
      }
      this.updateLayout(this.getActiveLayout(screen));
      this.updateAlignment(this.getActiveAlignment(screen));
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
